import { getAuth } from "firebase/auth";
import { doc, getDocFromServer } from "firebase/firestore";
import type { NavigationGuard } from "vue-router";
import { db } from "@/app/plugins/firebase";
import { useAuthStore } from "@/auth/stores/auth";
import { redirectMigratedAccount } from "@/auth/services/accountMigration";
import { useSnackBarStore } from "@/shared/stores/snackbar";
import i18n from "@/app/plugins/i18n";

/** Resolve the authenticated account before onboarding, enrollment or plan guards run. */
const migrationGuard: NavigationGuard = async (to) => {
  if (
    to.matched.some((record) => record.meta.skipMerchantGuards) ||
    !to.matched.some((record) => record.meta.requiresAuth)
  ) {
    return;
  }
  const { currentUser } = getAuth();
  if (!currentUser?.emailVerified) {
    return;
  }
  try {
    const { claims } = await currentUser.getIdTokenResult();
    const accountId = claims.account;
    const authStore = useAuthStore();
    if (authStore.pendingAccountId && authStore.pendingAccountId !== accountId) {
      return false;
    }
    if (claims.kioskSession === true || typeof accountId !== "string" || !accountId) {
      return;
    }
    // Do not decide from cached session storage or another membership's migration state.
    const account = await getDocFromServer(doc(db, "accounts", accountId));
    const currentClaims = (await currentUser.getIdTokenResult()).claims;
    if (
      getAuth().currentUser !== currentUser ||
      currentClaims.account !== accountId ||
      (authStore.pendingAccountId && authStore.pendingAccountId !== accountId)
    ) {
      return false;
    }
    if (redirectMigratedAccount({ migratedToBeta: account.get("migratedToBeta") === true })) {
      return false;
    }
  } catch {
    useSnackBarStore().toastError({ message: i18n.global.t("migration.statusUnavailable") });
    return false;
  }
};

export default migrationGuard;
